import type { UserRole } from '@coreltb/shared/constants';

/**
 * Codzienne powitanie (DailyWelcome) — TĘ LISTĘ EDYTUJEMY MY.
 * Dopisujesz / usuwasz wpis, wdrażasz — działa od następnego ranka.
 *
 * Styl (Dawid 2026-09-30): krótko, po ludzku, śmiesznie — jak od kolegi z biura.
 * NIE: nadęte frazesy, „fajrant”, wymyślone sytuacje z klientami.
 *
 * Pola:
 *  - `roles`     — tylko dla tych ról (brak = wszyscy),
 *  - `weekdays`  — tylko w te dni (1 = pn … 5 = pt; brak = zawsze),
 *  - `months`    — tylko w te miesiące (1–12; np. grill tylko latem),
 *  - `forEmails` — tylko dla tych osób (e-mail z Ustawień). Osoba z własnymi
 *                  hasłami widzi je częściej niż ogólne (patrz DailyWelcome).
 */
export interface WelcomeMessage {
  text: string;
  roles?: readonly UserRole[];
  weekdays?: readonly number[];
  months?: readonly number[];
  forEmails?: readonly string[];
}

const SALES: readonly UserRole[] = ['handlowiec', 'dyrektor_sprzedazy', 'admin', 'developer'];
const SUMMER = [6, 7, 8] as const;
const NOT_SUMMER = [1, 2, 3, 4, 5, 9, 10, 11, 12] as const;

/** Tomek — prezes i administrator. */
const TOMEK = ['tomasz.ledzki@coreltb.pl'] as const;

/** Julka — marketing: Facebook, Instagram, TikTok, grafiki, kampanie reklamowe. */
const JULKA = ['biuro@coreltb.pl'] as const;

/** `{name}` = imię (albo zdrobnienie z NICKNAMES). Losujemy jedno na dzień. */
export const GREETINGS: readonly string[] = [
  'Siema, {name}!',
  'Cześć, {name}!',
  'Hej, {name}!',
  'Hejka, {name}!',
  'Witaj, {name}!',
  'Jak tam, {name}?',
  'Dzień dobry, {name}!',
  'Miło Cię widzieć, {name}!',
  'O, {name}! Dobrze, że jesteś.',
  'No hej, {name}!',
];

/** Własne powitania dla konkretnych osób (zastępują ogólne). */
export const GREETINGS_FOR: Readonly<Record<string, readonly string[]>> = {
  'tomasz.ledzki@coreltb.pl': [
    'Dzień dobry, Prezesie!',
    'Witamy, Szefie!',
    'Szacuneczek, Prezes!',
    'Jak tam, Tomuś?',
    'Siemanko, Tomaszu!',
    'Cześć, Tomek!',
  ],
};

export const WELCOME_MESSAGES: readonly WelcomeMessage[] = [
  // — Przysłowia, tylko trochę poprawione
  { text: 'Nie od razu Kraków zbudowano. Ale gdyby dali go nam, stan surowy byłby do jesieni.' },
  { text: 'Wszędzie dobrze, ale w domu najlepiej. Dlatego je budujemy.' },
  { text: 'Kto rano wstaje, ten ma najlepsze miejsce na parkingu.' },
  { text: 'Mądry Polak po szkodzie. Mądry handlowiec — przed wysłaniem oferty.', roles: SALES },
  { text: 'Jak sobie wycenisz, tak zarobisz.', roles: SALES },
  { text: 'Co ma wisieć, nie utonie. Ale przypomnienie w CRM i tak ustaw.' },
  { text: 'Nie chwal oferty przed podpisem.', roles: SALES },
  { text: 'Pierwsze koty za płoty. Ale najpierw musi wjechać kawa.' },
  {
    text: 'Lepszy wróbel w garści niż gołąb na dachu. Chyba że dach jest nasz — tam zmieszczą się oba.',
  },
  { text: 'Kto pyta, nie błądzi. Kto dzwoni — sprzedaje.', roles: SALES },
  { text: 'Śpiesz się powoli. Chyba że klient czeka na ofertę.', roles: SALES },
  { text: 'Bez pracy nie ma kołaczy. Ani działki, ani domu, ani tarasu z widokiem.' },
  { text: 'Jak się nie ma, co się lubi, to się lubi poniedziałki. Podobno.', weekdays: [1] },
  { text: 'Najlepszy moment na budowę domu był 20 lat temu. Drugi najlepszy jest teraz.' },

  // — Polskie komedie (krótko, z podpisem)
  {
    text: '„Nie mamy pańskiego płaszcza i co pan nam zrobi?” — tak NIE obsługujemy klientów. (Miś)',
  },
  {
    text: '„Łubu dubu, łubu dubu, niech żyje nam prezes naszego klubu!” — pozdrowienia dla szefostwa. (Miś)',
  },
  {
    text: '„Ciemność, widzę ciemność!” — tryb ciemny włączysz w menu profilu. (Seksmisja)',
  },

  // — Życie biura
  { text: 'Kawa wypita? To zaczynamy. Niewypita? To najpierw kawa.' },
  { text: 'Nie ma głupich pytań. Są tylko pytania zadane w piątek o 14:55.' },
  { text: 'Dziś nie musi być idealnie. Wystarczy, że będzie zrobione.' },
  { text: 'Jak nie wiesz, co powiedzieć, powiedz prawdę. Łatwiej zapamiętać.' },
  { text: 'Zapisz od razu. „Zapamiętam” to najczęstsze kłamstwo w każdym biurze.' },
  { text: 'Telefon naładowany, kawa gorąca, leady czekają. Jedziemy.' },
  {
    text: 'Gdyby narzekanie budowało domy, bylibyśmy liderem rynku. Na szczęście budujemy normalnie.',
  },
  { text: 'Plan na dziś: zrobić więcej niż wczoraj. Ale to tylko plan.' },

  // — Dni tygodnia
  { text: 'Poniedziałek. Nikt go nie zamawiał, a jednak przyszedł.', weekdays: [1] },
  { text: 'Wtorek — jeszcze nie weekend, ale już nie poniedziałek. Sukces.', weekdays: [2] },
  { text: 'Środa. Połowa tygodnia za nami, połowa kawy przed nami.', weekdays: [3] },
  { text: 'Czwartek, czyli prawie piątek. Weekend już widać zza rogu.', weekdays: [4] },
  { text: 'Piątek! Oficjalnie wolno już myśleć o grillu.', weekdays: [5], months: SUMMER },
  { text: 'Piątek! Oficjalnie wolno już myśleć o weekendzie.', weekdays: [5], months: NOT_SUMMER },
  { text: 'Piątek. Jeszcze jeden telefon i weekend.', weekdays: [5] },

  // — Przydatne, ale po ludzku
  { text: 'Szukasz klienta? Ctrl+K i wpisz cokolwiek — telefon, miasto, numer oferty.' },
  {
    text: 'Wysłałeś ofertę? CRM sam przypomni Ci za 2 dni robocze. Możesz spać spokojnie.',
    roles: SALES,
  },
  {
    text: 'Pliki do leada wrzucisz, przeciągając je na kartę z dokumentami. Bez klikania po folderach.',
  },

  // — Tomek (prezes). Stanowczy Skorpion, inżynier, firma zbudowana od zera.
  { text: 'W tej firmie są dwa zdania: Twoje i błędne.', forEmails: TOMEK },
  { text: 'Twoje „nie” ma nośność stropu żelbetowego.', forEmails: TOMEK },
  {
    text: 'Negocjacje z Tobą są jak z grawitacją. Można próbować, ale wiadomo, kto wygra.',
    forEmails: TOMEK,
  },
  {
    text: 'Skorpiony nie zapominają. CRM ma przypomnienia tylko po to, żeby nadążyć.',
    forEmails: TOMEK,
  },
  {
    text: 'Horoskop dla Skorpiona na dziś: wszystko pójdzie po Twojemu. Jutro zresztą też.',
    forEmails: TOMEK,
  },
  {
    text: 'Skorpion w biurze. Kto chciał dziś dyskutować, niech to jeszcze przemyśli.',
    forEmails: TOMEK,
  },
  {
    text: 'Inżynier, prezes i Skorpion w jednej osobie. Oby dziś nikt się nie spóźnił.',
    forEmails: TOMEK,
  },
  {
    text: 'Najlepsze auto, najlepsza kawa, najlepsza firma. Przypadek? Nie sądzę.',
    forEmails: TOMEK,
  },
  { text: 'Prezes w pracy. Leady same akceptują oferty.', forEmails: TOMEK },
  {
    text: '„Niech żyje nam prezes naszego klubu!” — tym razem bez ironii. (Miś)',
    forEmails: TOMEK,
  },
  {
    text: 'Wiadomo, kto tu rządzi. CRM też wie — dlatego jesteś w nim administratorem.',
    forEmails: TOMEK,
  },
  {
    text: 'Nikt nie wchodzi Ci na głowę. Najwyżej na rusztowanie, i to w kasku.',
    forEmails: TOMEK,
  },
  {
    text: 'Kto zbudował firmę od zera, ten nie musi nikomu niczego udowadniać. Ale i tak udowadnia.',
    forEmails: TOMEK,
  },

  // — Julka (marketing: posty, grafiki, kampanie na Facebooku, Instagram, TikTok).
  {
    text: 'Twoje kampanie łowią leady, handlowcy je sprzedają. Bez Ciebie mieliby tylko kawę.',
    forEmails: JULKA,
  },
  {
    text: 'Hasztag #motywacja jeszcze nikomu nie pomógł. Kawa — owszem.',
    forEmails: JULKA,
  },
  {
    text: 'Mówią, że budżet reklamowy nie jest z gumy. A Ty i tak go rozciągasz.',
    forEmails: JULKA,
  },
  { text: 'Trend na TikToku żyje krócej niż mleko w biurowej lodówce.', forEmails: JULKA },
  { text: 'Dobry post sam się nie opublikuje. No to działamy.', forEmails: JULKA },
  {
    text: 'Nie każdy bohater nosi pelerynę. Niektórzy prowadzą firmowego Facebooka.',
    forEmails: JULKA,
  },
  {
    text: 'Ładna grafika to połowa sukcesu. Druga połowa to „opublikuj”.',
    forEmails: JULKA,
  },
  {
    text: 'Zróbmy dziś coś, co konkurencja będzie chciała zrobić jutro.',
    forEmails: JULKA,
  },
  { text: 'Nie wszystko musi być viralem. Ale próbować warto.', forEmails: JULKA },
];

/**
 * Jak mówimy do siebie w biurze — klucz = imię z Ustawień, wartość = formy
 * (kilka = losujemy). Brak wpisu = imię tak, jak jest wpisane.
 */
export const NAME_FORMS: Readonly<Record<string, readonly string[]>> = {
  Tomasz: ['Tomek'],
  Julia: ['Julia', 'Julka'],
  Jakub: ['Kuba'],
};

/**
 * Imię na sztywno dla konkretnego konta (klucz = e-mail, małymi literami) —
 * gdy w Ustawieniach nie ma prawdziwego imienia.
 */
export const NICKNAMES: Readonly<Record<string, string>> = {
  'dawidfc@gmail.com': 'Dawid',
};
